import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { buildSuggestionItems, getSuggestionKey, hasOverlappingBills, isAwaitingSpikeReview, useAllocatedTables, useAllocationComparisons, useAllocationSuggestions, usePropertyGoodwillDeductions } from 'src/components/allocations/AllocationSuggestion';
import { useStore } from 'src/stores';
import { buildAllocationTables, hasAllocationTable, indexAllocationStates, isApprovable, isHeldForReview, isPendingAllocation, withAllocations } from './allocationTables';
import useStyles from './billDetailsStyles';
import { withAllocationSetting } from './billEditUtils';
import { matchBillUtilities, selectServices } from './billUtilityMatching';
import { ConfirmActionModal } from './ConfirmActionModal';
import { PropertyAllocation } from './PropertyAllocation';
import { useBillAllocations } from './useBillAllocations';
const APPROVE_ALL_CONFIRM_MESSAGE = 'This will allocate every pending service on this bill across the listed properties and utilities, and ' + 'charge the residents shown. Please confirm that the amounts and resident charges have been reviewed.';
const APPROVE_ALL_HINT = 'Creates the allocation for each pending service and approves it once it is worked out.';
const REVIEW_ALL_HINT = 'Some services on this bill rose sharply on the last one. Review the comparison shown against each of ' + 'them to allocate the rest of the bill.';

/**
 * Allocation tables for every property/utility on the bill (Figma: "Allocation suggestions" card). The
 * charges come from the allocation service, which calculates them without saving anything, so editing
 * an input asks it again and the tables follow.
 */
export const AllocationSuggestionsCard = observer(_ref => {
  let billId = _ref.billId,
    currency = _ref.currency,
    data = _ref.data,
    onSaveSettings = _ref.onSaveSettings,
    properties = _ref.properties,
    propertyIds = _ref.propertyIds,
    settings = _ref.settings;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    draftSettings = _useState2[0],
    setDraftSettings = _useState2[1];
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    targetOverrides = _useState4[0],
    setTargetOverrides = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isApproveAllConfirmOpen = _useState6[0],
    setIsApproveAllConfirmOpen = _useState6[1];
  const isEditing = draftSettings !== null;
  const activeSettings = isEditing ? draftSettings : settings;

  // Only what the property is set up for is allocated; the rest of the bill is shown above.
  const services = useMemo(() => selectServices(matchBillUtilities({
    billPropertyIds: propertyIds,
    data,
    properties,
    targetOverrides
  }), true), [data, properties, propertyIds, targetOverrides]);
  const goodwillDeductions = usePropertyGoodwillDeductions(services.flatMap(group => group.services.map(_ref2 => {
    let target = _ref2.target;
    return target.propertyId;
  })));
  const groups = useMemo(() => buildAllocationTables({
    data,
    goodwillDeductions,
    groups: services,
    settings: activeSettings
  }), [activeSettings, data, goodwillDeductions, services]);
  const _useBillAllocations = useBillAllocations(billId),
    allocate = _useBillAllocations.allocate,
    allocationError = _useBillAllocations.error,
    isBusy = _useBillAllocations.isBusy,
    allocationServices = _useBillAllocations.services;
  const allocationStates = useMemo(() => indexAllocationStates(allocationServices), [allocationServices]);
  // What was actually allocated, for the services far enough along to have a table of their own.
  const allocatedTables = useAllocatedTables(allocationServices.filter(hasAllocationTable));
  // A service held back for review cannot be approved until its comparison has been looked at.
  const _useAllocationCompari = useAllocationComparisons(allocationServices.filter(isHeldForReview)),
    comparisons = _useAllocationCompari.comparisons,
    review = _useAllocationCompari.review,
    reviewingKeys = _useAllocationCompari.savingKeys;

  // Nothing is suggested for a service that is already allocated: its table is the real one.
  const items = useMemo(() => buildSuggestionItems(groups.flatMap(group => group.utilities).filter(table => !allocatedTables["".concat(table.propertyId, ":").concat(table.utilityId)])), [allocatedTables, groups]);
  const _useAllocationSuggest = useAllocationSuggestions(items),
    hasFailed = _useAllocationSuggest.hasFailed,
    isLoading = _useAllocationSuggest.isLoading,
    suggestions = _useAllocationSuggest.suggestions;
  const allocations = withAllocations(groups, {
    allocatedTables,
    allocationStates,
    comparisons,
    // Read here, inside the observer, so late-arriving resident names re-render the tables.
    getResidentName: accountId => store.account.profilesFullName.get(accountId),
    hasFailed,
    isLoading,
    reviewingKeys,
    suggestions
  });
  const tables = allocations.flatMap(allocation => allocation.utilities || []);
  const approvableTables = tables.filter(isApprovable);
  const pendingTables = approvableTables.filter(isPendingAllocation);
  // Held back by the allocation service until the rise on the last bill has been compared.
  const heldTables = tables.filter(isAwaitingSpikeReview);
  // One utility billed twice over is enough to hold the whole bill back: approving the rest in bulk
  // would quietly leave a bill that cannot be saved as it stands.
  const billedTables = tables.filter(hasOverlappingBills);
  if (!tables.length) return null;
  const propertyOptions = properties.map(property => ({
    id: property.propertyId,
    label: property.propertyAddress || property.propertyName || property.propertyId
  }));
  const handleChangeSetting = (propertyIndex, serviceType, patch) => setDraftSettings(current => withAllocationSetting(current, propertyIndex, serviceType, patch));
  const handleSelectTarget = (key, patch) => setTargetOverrides(current => _objectSpread(_objectSpread({}, current), {}, {
    [key]: _objectSpread(_objectSpread({}, current[key] || {}), patch)
  }));
  const handleSave = () => {
    onSaveSettings(draftSettings);
    setDraftSettings(null);
  };

  // Recorded against the cycle the service was held in, which is where the comparison was read.
  const handleReview = key => {
    const table = tables.find(item => item.key === key);
    const allocation = allocationStates[getSuggestionKey(table === null || table === void 0 ? void 0 : table.propertyId, table === null || table === void 0 ? void 0 : table.utilityId)];
    if (allocation !== null && allocation !== void 0 && allocation.billingCycleId) {
      review({
        billingCycleId: allocation.billingCycleId,
        propertyId: allocation.propertyId,
        utilityId: allocation.utilityId
      });
    }
  };

  // Allocated on the values the table was suggested on: what was shown is what gets charged.
  const handleApprove = key => allocate(buildSuggestionItems(tables.filter(table => table.key === key)));
  const handleApproveAll = () => {
    setIsApproveAllConfirmOpen(false);
    allocate(buildSuggestionItems(pendingTables));
  };
  const allocatedCount = approvableTables.filter(table => table.isApproved).length;

  /**
   * An approved table has already charged residents; the settings behind it no longer decide anything.
   * `isBusy` closes the same door while an allocation is on its way, where an edit lands after the
   * request that would have carried it and is lost without a trace.
   */
  const editBlockedReason = (() => {
    if (isBusy) return 'This bill is being allocated. The allocations can be edited again if it does not go through.';
    if (allocatedCount) return 'These allocations have been approved and can no longer be edited.';
    return null;
  })();

  // Once something has been allocated the card is no longer only a suggestion.
  const title = (() => {
    if (isEditing) return 'Edit allocation suggestions';
    return allocatedCount ? 'Allocations' : 'Allocation suggestions';
  })();
  const status = (() => {
    if (billedTables.length) return {
      className: classes.statusChipWarning,
      label: 'Service dates already billed'
    };
    if (!approvableTables.length) {
      return {
        className: '',
        label: isLoading ? 'Calculating' : 'Nothing to allocate'
      };
    }
    if (allocatedCount === approvableTables.length) return {
      className: classes.statusChipDone,
      label: 'Allocated'
    };
    return {
      className: '',
      label: "".concat(allocatedCount, "/").concat(approvableTables.length, " allocated")
    };
  })();
  const approveAllHint = (() => {
    if (billedTables.length) {
      return 'Some service dates on this bill are already billed, so nothing can be allocated until they change.';
    }
    if (heldTables.length) return REVIEW_ALL_HINT;
    return APPROVE_ALL_HINT;
  })();

  // A service still to be reviewed is not allocated, whatever else on the bill is.
  const approveAllLabel = pendingTables.length || heldTables.length || !approvableTables.length ? 'Approve and allocate all' : 'All allocated';
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.card, " ").concat(classes.cardTinted)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, title), isEditing ? null : /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(status.className)
  }, status.label)), isEditing ? null :
  /*#__PURE__*/
  // Once a table is approved its charges are on residents' ledgers, and editing the settings
  // behind it changes nothing that anyone will read. `isBusy` covers the same window while an
  // allocation is still in flight, where an edit is discarded without trace.
  React.createElement(ExpandedTooltip, {
    placement: "top",
    title: editBlockedReason !== null && editBlockedReason !== void 0 ? editBlockedReason : '',
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit allocations",
    className: classes.iconButton,
    disabled: !onSaveSettings || !!editBlockedReason,
    onClick: () => setDraftSettings(settings || {}),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))))), allocationError ? /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchBox, " ").concat(classes.matchBoxWarning)
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.matchText, " ").concat(classes.matchTextWarning)
  }, allocationError))) : null, allocations.map((allocation, index) => /*#__PURE__*/React.createElement(PropertyAllocation, {
    allocation: allocation,
    currency: currency,
    defaultExpanded: index === 0,
    isEditing: isEditing,
    key: allocation.address || index,
    onApprove: billId && !isBusy ? handleApprove : undefined,
    onChangeSetting: handleChangeSetting,
    onReview: handleReview,
    onSelectTarget: handleSelectTarget,
    propertyOptions: propertyOptions
  })), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDraftSettings(null),
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, "Save")) : /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: approveAllHint,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !billId || isBusy || !pendingTables.length || !!billedTables.length,
    onClick: () => setIsApproveAllConfirmOpen(true),
    size: "small",
    variant: "contained"
  }, approveAllLabel))), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Approve and allocate all",
    isOpen: isApproveAllConfirmOpen,
    message: APPROVE_ALL_CONFIRM_MESSAGE,
    onClose: () => setIsApproveAllConfirmOpen(false),
    onConfirm: handleApproveAll,
    title: "Approve and allocate all charges?"
  })));
});